import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import SolutionHero from '../components/solution/SolutionHero';
import SolutionDetails from '../components/solution/SolutionDetails';
import RelatedSolutions from '../components/solution/RelatedSolutions';
import { fetchSolutionBySlug, fetchSolutions } from '../services/api';

const SolutionPage = () => {
  const { solutionSlug } = useParams();
  const [solution, setSolution] = useState(null);
  const [allSolutions, setAllSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const loadSolutionData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch solution by slug
        const solutionData = await fetchSolutionBySlug(solutionSlug);
        
        if (!solutionData) {
          console.warn(`Solution not found for slug: ${solutionSlug}`);
          setNotFound(true);
          setLoading(false);
          return;
        }
        
        // Normalize solution data structure
        const normalizedSolution = {
          id: solutionData.id,
          Title: solutionData.Title,
          slug: solutionData.slug,
          Description: solutionData.Description,
          ...solutionData
        };
        
        setSolution(normalizedSolution);
        
        // Fetch all solutions for related solutions section
        try {
          const solutions = await fetchSolutions();
          setAllSolutions(solutions);
        } catch (err) {
          console.error('Error loading related solutions:', err);
          // Don't fail the whole page if related solutions fail
        }
        
      } catch (err) {
        console.error('Error loading solution data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (solutionSlug) {
      loadSolutionData();
    }
  }, [solutionSlug]);

  // Redirect to solutions page if solution not found
  if (notFound) {
    return <Navigate to="/solutions-we-provide/all" replace />;
  }

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-catalina-blue mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading solution...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8">
            <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Solution</h2>
            <p className="text-red-500 mb-4">{error}</p>
            <a
              href="/solutions-we-provide/all"
              className="inline-block bg-catalina-blue text-white font-semibold py-2 px-6 rounded-lg hover:bg-san-marino transition duration-300"
            >
              Back to Solutions
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SolutionHero solution={solution} />
      <SolutionDetails solution={solution} />
      <RelatedSolutions solutions={allSolutions} currentSolutionId={solution?.id} />
    </div>
  );
};

export default SolutionPage;